import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import JewelForm from '../components/JewelForm';
import AdminNav from '../components/AdminNav';
import { formatInrWhole } from '../utils/money';
import './AdminDashboard.css';

function flattenCategoryRows(tree, depth = 0) {
  const rows = [];
  for (const node of tree) {
    const { subcategories, ...rest } = node;
    rows.push({ ...rest, depth });
    if (subcategories?.length) {
      rows.push(...flattenCategoryRows(subcategories, depth + 1));
    }
  }
  return rows;
}

function forbiddenParentIdsForCategory(tree, editId) {
  const forbidden = new Set([editId]);
  function collectUnder(node) {
    for (const s of node.subcategories || []) {
      forbidden.add(s.id);
      collectUnder(s);
    }
  }
  function find(nodes) {
    for (const n of nodes) {
      if (n.id === editId) {
        collectUnder(n);
        return true;
      }
      if (find(n.subcategories || [])) return true;
    }
    return false;
  }
  find(tree);
  return forbidden;
}

function allCategoriesWithLabels(tree, prefix = '') {
  const out = [];
  for (const n of tree) {
    const label = prefix ? `${prefix} › ${n.name}` : n.name;
    out.push({ id: n.id, label });
    out.push(...allCategoriesWithLabels(n.subcategories || [], label));
  }
  return out;
}

async function parseApiJson(res) {
  const text = await res.text();
  if (!text) return { ok: res.ok, data: {} };
  try {
    return { ok: res.ok, data: JSON.parse(text) };
  } catch {
    return {
      ok: res.ok,
      data: null,
      notJson: true,
      status: res.status,
      snippet: text.slice(0, 160).replace(/\s+/g, ' '),
    };
  }
}

export default function AdminDashboard() {
  const { admin, loading: authLoading } = useAuth();
  const [jewels, setJewels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingJewel, setEditingJewel] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [liveRates, setLiveRates] = useState({ gold: null, silver: null });
  const [categoryTree, setCategoryTree] = useState([]);
  const [categoryForm, setCategoryForm] = useState(null);
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [jewelSearch, setJewelSearch] = useState('');
  const [hideSoldJewels, setHideSoldJewels] = useState(false);

  const fetchLiveRates = useCallback(async () => {
    try {
      const res = await fetch('/api/live-prices');
      if (!res.ok) return;
      const data = await res.json();
      setLiveRates({ gold: data.gold, silver: data.silver });
    } catch {
      // ignore
    }
  }, []);

  const fetchJewels = useCallback(async () => {
    try {
      const res = await fetch('/api/jewels', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setJewels(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch('/api/categories');
      if (!res.ok) throw new Error('Failed to load categories');
      setCategoryTree(await res.json());
    } catch {
      setCategoryTree([]);
    }
  }, []);

  useEffect(() => {
    fetchJewels();
    fetchLiveRates();
    fetchCategories();
  }, [fetchJewels, fetchLiveRates, fetchCategories]);

  const filteredJewels = useMemo(() => {
    const q = jewelSearch.trim().toLowerCase();
    if (!q) return jewels;
    return jewels.filter((j) => {
      const name = (j.name || '').toLowerCase();
      const mat = (j.material || '').toLowerCase();
      const idStr = String(j.id);
      const itemNo = String(j.item_number || '').toLowerCase();
      return name.includes(q) || mat.includes(q) || idStr.includes(q) || itemNo.includes(q);
    });
  }, [jewels, jewelSearch]);

  const displayJewels = useMemo(() => {
    if (!hideSoldJewels) return filteredJewels;
    return filteredJewels.filter((j) => !j.is_sold);
  }, [filteredJewels, hideSoldJewels]);

  if (authLoading) return <div className="page-center"><p>Loading...</p></div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const handleCreate = async (formData) => {
    setSaving(true); setError('');
    try {
      const res = await fetch('/api/jewels', { method: 'POST', credentials: 'include', body: formData });
      const parsed = await parseApiJson(res);
      if (parsed.notJson) {
        setError(
          parsed.status === 413
            ? 'Upload is too large for the server. Use smaller images or fewer extra photos, then try again.'
            : `Server did not return JSON (HTTP ${parsed.status}). Often this is an upload size limit or proxy error — check deploy/nginx client_max_body_size and PM2 logs. ${parsed.snippet ? `(${parsed.snippet}…)` : ''}`
        );
        return;
      }
      const { data } = parsed;
      if (!res.ok) throw new Error(data?.error || 'Failed to create');
      setJewels((prev) => [data, ...prev.filter((j) => j.id !== data.id)]);
      setShowForm(false);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (formData) => {
    setSaving(true); setError('');
    try {
      const res = await fetch(`/api/jewels/${editingJewel.id}`, { method: 'PUT', credentials: 'include', body: formData });
      const parsed = await parseApiJson(res);
      if (parsed.notJson) {
        setError(
          parsed.status === 413
            ? 'Upload is too large for the server. Use smaller images or fewer extra photos, then try again.'
            : `Server did not return JSON (HTTP ${parsed.status}). ${parsed.snippet ? `(${parsed.snippet}…)` : ''}`
        );
        return;
      }
      const { data } = parsed;
      if (!res.ok) throw new Error(data?.error || 'Failed to update');
      setJewels((prev) => prev.map((j) => (j.id === data.id ? data : j)));
      setEditingJewel(null); setShowForm(false);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this jewel?')) return;
    try {
      const res = await fetch(`/api/jewels/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed to delete'); }
      fetchJewels();
    } catch (err) { setError(err.message); }
  };

  const toggleJewelAvailability = async (jewel) => {
    setError('');
    try {
      const res = await fetch(`/api/jewels/${jewel.id}/availability`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_sold: !jewel.is_sold }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Update failed');
      fetchJewels();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRefreshPrices = async () => {
    try {
      const res = await fetch('/api/live-prices/refresh', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        await fetchLiveRates();
        fetchJewels();
        alert(`Prices refreshed! Gold: ₹${Math.round(Number(data.gold))}/g, Silver: ₹${Math.round(Number(data.silver))}/g`);
      } else {
        alert(data.error || 'Failed to refresh');
      }
    } catch {
      alert('Error refreshing prices');
    }
  };

  const formatRateTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
  };

  const startEdit = async (jewel) => {
    try {
      const res = await fetch(`/api/jewels/${jewel.id}`, { credentials: 'include' });
      if (res.ok) {
        setEditingJewel(await res.json());
        setShowForm(true);
        return;
      }
    } catch {
      // fall through
    }
    setEditingJewel(jewel);
    setShowForm(true);
  };
  const cancelForm = () => { setShowForm(false); setEditingJewel(null); };

  const categoryRows = flattenCategoryRows(categoryTree);
  const parentSelectForbidden =
    categoryForm?.mode === 'edit' && categoryForm.id != null
      ? forbiddenParentIdsForCategory(categoryTree, categoryForm.id)
      : new Set();
  const parentSelectOptions = [
    { id: '', label: '— Top level —' },
    ...allCategoriesWithLabels(categoryTree).filter((o) => !parentSelectForbidden.has(o.id)),
  ];

  const submitCategoryForm = async (e) => {
    e.preventDefault();
    if (!categoryForm) return;
    setCategorySaving(true);
    setCategoryError('');
    const body = {
      name: categoryForm.name.trim(),
      parent_id: categoryForm.parent_id === '' ? null : Number(categoryForm.parent_id),
      sort_order: categoryForm.sort_order === '' ? 0 : Number(categoryForm.sort_order),
      material: categoryForm.mode === 'create' ? null : categoryForm.material ?? null,
    };
    try {
      const url =
        categoryForm.mode === 'edit'
          ? `/api/categories/${categoryForm.id}`
          : '/api/categories';
      const res = await fetch(url, {
        method: categoryForm.mode === 'edit' ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setCategoryForm(null);
      await fetchCategories();
    } catch (err) {
      setCategoryError(err.message);
    } finally {
      setCategorySaving(false);
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category? Jewels using it will have category cleared.')) return;
    setCategoryError('');
    try {
      const res = await fetch(`/api/categories/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      await fetchCategories();
    } catch (err) {
      setCategoryError(err.message);
    }
  };

  return (
    <div className="admin-dashboard">
      <AdminNav />
      <div className="admin-header">
        <div>
          <h1>Catalog &amp; jewels</h1>
          <p className="admin-header-sub">Signed in as <strong>{admin.username}</strong></p>
        </div>
        <div className="admin-header-actions">
          <button type="button" className="btn btn-outline" onClick={handleRefreshPrices}>Refresh Prices</button>
        </div>
      </div>

      <div className="admin-live-rates" aria-label="Current metal rates">
        <div className="admin-live-rates-title">Current rates (per gram, ex-GST)</div>
        <div className="admin-live-rates-grid">
          <div className="admin-live-rate gold">
            <span className="admin-live-rate-label">Gold</span>
            {liveRates.gold ? (
              <>
                <span className="admin-live-rate-value">
                  ₹{Math.round(Number(liveRates.gold.price_per_gram)).toLocaleString('en-IN')}/g
                </span>
                <span className="admin-live-rate-time">{formatRateTime(liveRates.gold.fetched_at)}</span>
              </>
            ) : (
              <span className="admin-live-rate-missing">—</span>
            )}
          </div>
          <div className="admin-live-rate silver">
            <span className="admin-live-rate-label">Silver</span>
            {liveRates.silver ? (
              <>
                <span className="admin-live-rate-value">
                  ₹{Math.round(Number(liveRates.silver.price_per_gram)).toLocaleString('en-IN')}/g
                </span>
                <span className="admin-live-rate-time">{formatRateTime(liveRates.silver.fetched_at)}</span>
              </>
            ) : (
              <span className="admin-live-rate-missing">—</span>
            )}
          </div>
        </div>
        {!liveRates.gold && !liveRates.silver && (
          <p className="admin-live-rates-hint">No cached rates yet. Use &quot;Refresh Prices&quot; to fetch from the source.</p>
        )}
      </div>

      <section className="admin-categories-panel" aria-labelledby="admin-categories-heading">
        <h2 id="admin-categories-heading">Categories</h2>
          <p className="admin-categories-intro">
          Types such as chain, ring, or earring can be shared across Gold, Silver, Gold plated, and Fancy. Use subcategories under a top-level row if you need hierarchy.
        </p>
        {categoryError && <p className="admin-error admin-error-inline">{categoryError}</p>}
        {categoryForm && (
          <form className="admin-category-form" onSubmit={submitCategoryForm}>
            <h3>{categoryForm.mode === 'edit' ? 'Edit category' : 'New category'}</h3>
            <div className="admin-category-form-grid">
              <label className="admin-category-field">
                <span>Name *</span>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  required
                  placeholder="e.g. Chain, Ring, Earring"
                />
              </label>
              <label className="admin-category-field">
                <span>Parent</span>
                <select
                  value={categoryForm.parent_id === '' ? '' : String(categoryForm.parent_id)}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, parent_id: e.target.value })
                  }
                >
                  {parentSelectOptions.map((o) => (
                    <option key={o.id === '' ? 'none' : o.id} value={o.id === '' ? '' : String(o.id)}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-category-field">
                <span>Sort order</span>
                <input
                  type="number"
                  value={categoryForm.sort_order}
                  onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: e.target.value })}
                  placeholder="0"
                />
              </label>
            </div>
            <div className="admin-category-form-actions">
              <button type="submit" className="btn btn-primary" disabled={categorySaving}>
                {categorySaving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setCategoryForm(null); setCategoryError(''); }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
        {!categoryForm && (
          <button
            type="button"
            className="btn btn-outline admin-category-add"
            onClick={() =>
              setCategoryForm({ mode: 'create', name: '', parent_id: '', sort_order: '0' })
            }
          >
            + Add category
          </button>
        )}
        {categoryRows.length === 0 ? (
          <p className="admin-categories-empty">No categories yet. Add one to filter collections by type.</p>
        ) : (
          <div className="admin-categories-table-wrap">
            <table className="admin-categories-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Applies to</th>
                  <th>Sort</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {categoryRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span
                        className="admin-category-name-cell"
                        style={{ paddingLeft: `${row.depth * 1.1}rem` }}
                      >
                        {row.name}
                      </span>
                    </td>
                    <td className="admin-category-scope">
                      {!row.material ? 'All metals' : row.material}
                    </td>
                    <td>{row.sort_order ?? 0}</td>
                    <td className="admin-category-row-actions">
                      <button
                        type="button"
                        className="btn-edit"
                        onClick={() =>
                          setCategoryForm({
                            mode: 'edit',
                            id: row.id,
                            name: row.name,
                            parent_id: row.parent_id != null ? String(row.parent_id) : '',
                            sort_order: String(row.sort_order ?? 0),
                            material: row.material ?? null,
                          })
                        }
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-delete"
                        onClick={() => deleteCategory(row.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {error && <p className="admin-error">{error}</p>}

      {showForm ? (
        <div className="admin-form-section">
          <h2>{editingJewel ? 'Edit Jewel' : 'Add New Jewel'}</h2>
          <JewelForm
            jewel={editingJewel}
            onSubmit={editingJewel ? handleUpdate : handleCreate}
            onCancel={cancelForm}
            loading={saving}
          />
        </div>
      ) : (
        <>
          <button className="btn btn-primary add-btn" onClick={() => { setEditingJewel(null); setShowForm(true); }}>
            + Add New Jewel
          </button>
          <p className="admin-sold-explainer">
            Sold products stay in this list so you can mark them available again or keep history. They are hidden from the public site. Use &quot;Hide sold&quot; below to focus on active stock.
          </p>

          {loading ? (
            <p>Loading jewels...</p>
          ) : jewels.length === 0 ? (
            <p className="admin-empty">No jewels yet. Add your first one!</p>
          ) : (
            <div className="admin-jewels-table">
              <div className="admin-jewels-toolbar">
                <label className="admin-jewels-search-label" htmlFor="admin-jewel-search">
                  Search products
                </label>
                <input
                  id="admin-jewel-search"
                  type="search"
                  className="admin-jewels-search"
                  placeholder="Name, material, ID, or item #…"
                  value={jewelSearch}
                  onChange={(e) => setJewelSearch(e.target.value)}
                  autoComplete="off"
                />
                <label className="admin-hide-sold">
                  <input
                    type="checkbox"
                    checked={hideSoldJewels}
                    onChange={(e) => setHideSoldJewels(e.target.checked)}
                  />
                  Hide sold
                </label>
                <span className="admin-jewels-count">
                  {displayJewels.length === jewels.length
                    ? `${jewels.length} items`
                    : `${displayJewels.length} of ${jewels.length} shown`}
                </span>
              </div>
              {filteredJewels.length === 0 ? (
                <p className="admin-empty admin-empty-search">No jewels match your search. Try another term.</p>
              ) : displayJewels.length === 0 ? (
                <p className="admin-empty admin-empty-search">No items to show with current filters. Try unchecking &quot;Hide sold&quot;.</p>
              ) : (
              <table>
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Item #</th>
                    <th>Name</th>
                    <th>Material</th>
                    <th>Price</th>
                    <th>Weight</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayJewels.map(jewel => (
                    <tr key={jewel.id} className={jewel.is_sold ? 'admin-row-sold' : ''}>
                      <td>
                        {jewel.image_mime_type ? (
                          <img src={`/api/jewels/${jewel.id}/image`} alt={jewel.name} className="admin-thumb" />
                        ) : (
                          <span className="no-img">—</span>
                        )}
                      </td>
                      <td className="admin-item-num">{jewel.item_number || '—'}</td>
                      <td>{jewel.name}</td>
                      <td className="cap">{jewel.material}</td>
                      <td>{formatInrWhole(jewel.display_price)}</td>
                      <td>{jewel.weight_g ? `${jewel.weight_g}g` : '—'}</td>
                      <td className="admin-status-cell">
                        <span className={jewel.is_sold ? 'admin-badge admin-badge-sold' : 'admin-badge admin-badge-avail'}>
                          {jewel.is_sold ? 'Sold' : 'Available'}
                        </span>
                        <button
                          type="button"
                          className="btn-toggle-stock"
                          onClick={() => toggleJewelAvailability(jewel)}
                        >
                          {jewel.is_sold ? 'Mark available' : 'Mark sold'}
                        </button>
                      </td>
                      <td className="admin-actions">
                        <button type="button" className="btn-edit" onClick={() => startEdit(jewel)}>Edit</button>
                        <button type="button" className="btn-delete" onClick={() => handleDelete(jewel.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
